import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, useIntl } from 'react-intl';
import dayjs from 'dayjs';

import { DealDto } from 'api/investor-client';
import { ProductTypeChip, DealStatusChip, InlineTooltip, OriginatorRatingChip } from 'components/UI';
import { IDealDetails } from 'utils';
import { DealType } from 'types';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '40px',
    flexWrap: 'wrap',
    marginBottom: '16px',

    [theme.breakpoints.up('sm')]: {
      gap: '16px',
      marginBottom: 0,
    },

    [theme.breakpoints.up('md')]: {
      flexWrap: 'nowrap',
    },

    [theme.breakpoints.up('lg')]: {
      marginBottom: '24px',
    },
  },

  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    overflow: 'hidden',
  },

  subtitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px 24px',
    flexWrap: 'wrap',
  },

  subtitleTextGroup: {
    display: 'flex',
    gap: '8px',
  },

  dealInfoContainer: {
    maxWidth: '100%',
    height: 'fit-content',
    display: 'flex',
    flexShrink: 0,
    flexGrow: 1,
    flexWrap: 'wrap',
    gap: '16px',

    [theme.breakpoints.up('md')]: {
      flexGrow: 0,
      justifyContent: 'flex-start',
    },
  },

  dealInfoCard: {
    minHeight: '72px',
    padding: '16px',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.grey[100],
  },

  dealInfoGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    overflow: 'hidden',
  },

  dealInfoGroupText: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '4px',
    overflow: 'hidden',
  },

  metricTile: {
    minWidth: '120px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    gap: '4px',
  },

  originatorRatingInfoText: {
    display: 'flex',
    alignItems: 'center',
  },

  tooltipIcon: {
    marginLeft: '8px',
  },
}));

interface Props {
  deal: DealDto;
  dealDetails: IDealDetails;
}

const formatPercentage = (value?: number) => {
  return typeof value === 'number' ? `${(value * 100).toFixed(3)}%` : '~';
};

export const DealDetailsHeader = ({ deal, dealDetails }: Props) => {
  const classes = useStyles();
  const intl = useIntl();

  return (
    <Box className={classes.wrapper}>
      <Box className={classes.titleContainer}>
        <Typography variant="h5" fontWeight={600} noWrap>
          <FormattedMessage id="deal" /> {deal.id}
        </Typography>

        <Box className={classes.subtitleContainer}>
          <DealStatusChip status={deal.status} />

          <Box className={classes.subtitleTextGroup}>
            <Typography variant="body2" color="text.secondary">
              <FormattedMessage id="investment_date" />:
            </Typography>
            <Typography variant="subtitle3">
              {dealDetails.common.investorCommittedDate
                ? dayjs(dealDetails.common.investorCommittedDate).format('DD/MM/YYYY')
                : '~'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box className={classes.dealInfoContainer}>
        <Box className={`${classes.dealInfoCard} ${classes.metricTile}`}>
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage id="duration" />
          </Typography>

          <Typography variant="body2" fontWeight={600} noWrap>
            {deal.duration} <FormattedMessage id={deal.duration === 1 ? 'month' : 'months'} />
          </Typography>
        </Box>

        <Box className={`${classes.dealInfoCard} ${classes.metricTile}`}>
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage id="interest_rate.nominal.short" />
          </Typography>

          <Typography variant="body2" fontWeight={600} noWrap>
            {formatPercentage(deal.nominalInterestRate)}
          </Typography>
        </Box>

        <Box className={`${classes.dealInfoCard} ${classes.metricTile}`}>
          <Typography variant="caption" color="text.secondary">
            <FormattedMessage id="expected_default_rate.short" />
          </Typography>

          <Typography variant="body2" fontWeight={600} noWrap>
            {formatPercentage(deal.risk.defaultRate)}
          </Typography>
        </Box>

        <Box className={`${classes.dealInfoCard} ${classes.dealInfoGroup}`}>
          <OriginatorRatingChip rating={deal.providerCompositeScore} />

          <Box className={classes.originatorRatingInfoText}>
            <Typography variant="body2" noWrap>
              <FormattedMessage id="rating.provider" />
              <InlineTooltip
                tooltip={intl.formatMessage({ id: 'rating.provider.description_tooltip' })}
                size={14}
                className={classes.tooltipIcon}
              />
            </Typography>
          </Box>
        </Box>

        <Box className={`${classes.dealInfoCard} ${classes.dealInfoGroup}`}>
          <ProductTypeChip productType={deal.risk.productType as DealType} size="large" />

          <Box className={classes.dealInfoGroupText}>
            <Typography variant="caption" color="text.secondary">
              <FormattedMessage id="borrower.type" />
            </Typography>

            <Typography variant="body2" noWrap>
              <FormattedMessage id={`borrower.${deal.risk.productType}`} />
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};